// @ts-nocheck
import { useTranslation } from 'react-i18next';
import { Loader2, X, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { saveProspectNote } from '@/services/dialer/callNotes';
import { formatDate, formatDateTime, formatDurationClock } from '@/i18n/locale';

/**
 * L'onglet Notes de la fiche contact.
 *
 * Extrait de ProspectDetailPanel, qui faisait 2150 lignes : ses cinq onglets
 * y vivaient a la suite dans un seul composant, et rien ne disait de quoi
 * chacun avait besoin. Ses dependances sont maintenant ecrites.
 */

export default function ProspectNotesTab({
  prospect,
  parsedNotes,
  notes,
  setNotes,
  newNote,
  setNewNote,
  savingNote,
  setSavingNote,
  queryClient,
  liveCallId = null,
  liveCallNotes = '',
}) {
  const { t } = useTranslation(['admin', 'dialer', 'common']);

  // One save path for the button and Ctrl/Cmd+Enter. During a call to this
  // contact the note also lands in that call's call_notes (RUI-06).
  const addNote = async () => {
    if (!newNote.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const serialized = await saveProspectNote({
        prospectId: prospect.id, previous: parsedNotes, text: newNote, liveCallId, liveCallNotes,
      });
      setNotes(serialized);
      setNewNote('');
      queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
      if (liveCallId) {
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
        queryClient.invalidateQueries({ queryKey: ['prospect-calls'] });
      }
      toast.success(t('dialer:prospectPanel.toasts.noteAdded'));
    } catch (_e) {
      toast.error(t('dialer:prospectPanel.toasts.updateError'));
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="space-y-4">
        {/* Add new note */}
        <div className="space-y-2">
            <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={t('admin:components.prospectDetail.addNotePlaceholder')}
                className="min-h-[80px] text-sm"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && newNote.trim()) {
                        e.preventDefault();
                        void addNote();
                    }
                }}
            />
            <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground/60">{t('admin:components.prospectDetail.saveShortcut')}</p>
                <Button
                    size="sm"
                    disabled={!newNote.trim() || savingNote}
                    onClick={() => void addNote()}
                    className="bg-primary hover:bg-primary/90 text-white h-7 text-xs"
                >
                    {savingNote ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    {t('common:actions.add')}
                </Button>
            </div>
        </div>

        {/* Notes list */}
        {parsedNotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground/60 text-sm">
                {t('dialer:prospectPanel.notes.empty')}
            </div>
        ) : (
            <div className="space-y-2">
                {[...parsedNotes].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map((note, idx) => (
                    <div
                        key={idx}
                        className={`p-3 rounded-lg border text-sm ${
                            note.pinned ? 'border-amber-200 bg-amber-50/50' : 'border-border/50 surface-1/50'
                        }`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <p className="text-foreground whitespace-pre-wrap flex-1">{note.text}</p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    onClick={async () => {
                                        const updated = parsedNotes.map((n, i) => i === idx ? { ...n, pinned: !n.pinned } : n);
                                        const serialized = JSON.stringify(updated);
                                        await api.entities.ProspectQueue.update(prospect.id, { notes: serialized });
                                        setNotes(serialized);
                                        queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
                                    }}
                                    className={`p-1 rounded hover:bg-accent transition-colors ${note.pinned ? 'text-amber-500' : 'text-muted-foreground/40'}`}
                                    title={t(note.pinned ? 'dialer:prospectPanel.notes.unpin' : 'dialer:prospectPanel.notes.pin')}
                                >
                                    <StickyNote className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={async () => {
                                        const updated = parsedNotes.filter((_, i) => i !== idx);
                                        const serialized = JSON.stringify(updated);
                                        await api.entities.ProspectQueue.update(prospect.id, { notes: serialized });
                                        setNotes(serialized);
                                        queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
                                        toast.success(t('dialer:prospectPanel.toasts.noteDeleted'));
                                    }}
                                    className="p-1 rounded hover:bg-red-100 text-muted-foreground/40 hover:text-red-500 transition-colors"
                                    title={t('common:actions.delete')}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                        <p className="text-[10px] text-muted-foreground/60 mt-2">
                            {formatDate(note.timestamp, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                    </div>
                ))}
            </div>
        )}
    </div>
  );
}
